
import { Type, type Static, TSchema } from "@fastify/type-provider-typebox";
export const StatDateFrom = Type.Number();
export const StatDateTo = Type.Number();

export const StatDateRange = Type.Object({
  from: StatDateFrom,
  to: StatDateTo,
});

export type StatDateRange = Static<typeof StatDateRange>;

export const StatGranularity = Type.Union([Type.Literal("day"), Type.Literal("hour")]);

export type StatGranularity = Static<typeof StatGranularity>;

export const StatDataSeries = Type.Object({
  date: Type.Number(),
  value: Type.Number(),
  formattedDate: Type.Optional(Type.String()),
});

export type StatDataSeries = Static<typeof StatDataSeries>;

export const SalesSnapshotDetailData = Type.Object({
  name: Type.String(),
  value: Type.Number({ default: 0 }),
  type: Type.Union([Type.Literal("number"), Type.Literal("currency"), Type.Literal("percent")]),
  currency: Type.Union([Type.Null(), Type.String({ default: "USD" })]),
});

export type SalesSnapshotDetailData = Static<typeof SalesSnapshotDetailData>;

export const SalesSnapshotDetailItem = Type.Composite([SalesSnapshotDetailData, Type.Object({
  data: Type.Optional(Type.Array(SalesSnapshotDetailData)),
})]);

export type SalesSnapshotDetailItem = Static<typeof SalesSnapshotDetailItem>;

export const SalesSnapshotDetail = Type.Record(Type.String(), SalesSnapshotDetailItem);

export type SalesSnapshotDetail = Static<typeof SalesSnapshotDetail>;

export const SalesSnapshot = Type.Object({
  totalSales: Type.Number({ default: 0 }),
  orderCount: Type.Number({ default: 0 }),
  unitCount: Type.Number({ default: 0 }),
  refundCount: Type.Number({ default: 0 }),
  salesChangePercentage: Type.Optional(Type.Number({ default: 0 })),
  salesChangeInterval: Type.Optional(Type.String()),
  totalAdvertisingSpending: Type.Number({ default: 0 }),
  estimatedPayout: Type.Number({ default: 0 }),
  netProfit: Type.Number({ default: 0 }),
  detail: Type.Optional(SalesSnapshotDetail),
  from: StatDateFrom,
  to: StatDateTo,
  granularity: StatGranularity,
});

export type SalesSnapshot = Static<typeof SalesSnapshot>;

export const SellerSalesDetailSection = Type.Object({
  total: Type.Number({ default: 0 }),
  data: Type.Optional(Type.Record(Type.String(), Type.Number())),
});

export type SellerSalesDetailSection = Static<typeof SellerSalesDetailSection>;

export const SellerSalesDetail = Type.Object({
  sales: SellerSalesDetailSection,
  amazonFees: SellerSalesDetailSection,
  refundCosts: SellerSalesDetailSection,
  shippingCosts: SellerSalesDetailSection,
  promotions: SellerSalesDetailSection,
  costOfGoods: SellerSalesDetailSection,
  expenses: SellerSalesDetailSection,
  grossProfit: SellerSalesDetailSection,
  netProfit: SellerSalesDetailSection,
  estimatedPayout: SellerSalesDetailSection,
  units: SellerSalesDetailSection,
  percentageRefunds: SellerSalesDetailSection,
  margin: SellerSalesDetailSection,
  roi: SellerSalesDetailSection,
});

export type SellerSalesDetailKey = keyof SellerSalesDetail;

export type SellerSalesDetail = Static<typeof SellerSalesDetail>;

export const SellerSalesData = Type.Object({
  totalSales: Type.Number({ default: 0 }),
  netProfit: Type.Number({ default: 0 }),
  grossProfit: Type.Number({ default: 0 }),
  estimatedPayout: Type.Number({ default: 0 }),
  unitCount: Type.Number({ default: 0 }),
  refundCount: Type.Number({ default: 0 }),
  orderCount: Type.Number({ default: 0 }),
  detail: SellerSalesDetail
});

export type SellerSalesData = Static<typeof SellerSalesData>;


export const SalesSnapShotInterval = Type.Object({
  from: StatDateFrom,
  to: StatDateTo,
  granularity: StatGranularity,
});

export type SalesSnapShotInterval = Static<typeof SalesSnapShotInterval>;

export const SellerSalesSnapshot = Type.Composite([SalesSnapShotInterval, Type.Object({
  data: SellerSalesData,
})])

export type SellerSalesSnapshot = Static<typeof SellerSalesSnapshot>;

export const SalesSnapshotRequestBody = Type.Object({
  intervals: Type.Array(
    SalesSnapShotInterval
  ),
});

export type SalesSnapshotRequestBody = Static<typeof SalesSnapshotRequestBody>;

export const SalesSnapshotResponseBody = Type.Array(
  SellerSalesSnapshot
);

export type SalesSnapshotResponseBody = Static<typeof SalesSnapshotResponseBody>;


export const SalesBreakdown = Type.Object({
  date: Type.Number({ default: 0 }),
  granularity: StatGranularity,
  totalSales: Type.Number({ default: 0 }),
  unitCount: Type.Number({ default: 0 }),
  refundCount: Type.Number({ default: 0 }),
  netProfit: Type.Number({ default: 0 }),
});

export type SalesBreakdown = Static<typeof SalesBreakdown>;

export const SalesBreakdownRequestBody = Type.Object({
  from: StatDateFrom,
  to: StatDateTo,
  granularity: StatGranularity,
});

export type SalesBreakdownRequestBody = Static<typeof SalesBreakdownRequestBody>;

export const SalesBreakdownResponseBody = Type.Array(SalesBreakdown);

export type SalesBreakdownResponseBody = Static<typeof SalesBreakdownResponseBody>;

export const FinancialSnapshotRequestBody = Type.Object({
  intervals: Type.Array(
    SalesSnapShotInterval
  ),
});

export const ProductSalesLimit = Type.Union([Type.Literal(5), Type.Literal(10), Type.Literal(20)]);

export type ProductSalesLimit = Static<typeof ProductSalesLimit>;

export const ProductSalesQuery = Type.Object({
  from: StatDateFrom,
  to: StatDateTo,
});

export type ProductSalesQuery = Static<typeof ProductSalesQuery>;

export const ProductSalesItem = Type.Object({
  productId: Type.Number(),
  image: Type.String(),
  name: Type.String(),
  sku: Type.String(),
  asin: Type.String(),
  totalSales: Type.Number(),
  totalUnits: Type.Number(),
  totalShipped: Type.Number(),
  totalCogs: Type.Number(),
  totalAmazonFees: Type.Number(),
  totalRefundCosts: Type.Number(),
  totalShippingCosts: Type.Number(),
  totalPromotions: Type.Number(),
  totalRefundCount: Type.Number(),
  grossProfit: Type.Number(),
  estimatedPayout: Type.Number(),
  percentageRefunds: Type.Number(),
  margin: Type.Number(),
  roi: Type.Number(),
});

export type ProductSalesItem = Static<typeof ProductSalesItem>;

export const ProductSalesResponseBody = Type.Object({
  products: Type.Array(
    ProductSalesItem
  ),
  total: Type.Number({ default: 0 }),
});

export type ProductSalesResponseBody = Static<typeof ProductSalesResponseBody>;

export const SkuSalesQuery = Type.Object({
  from: StatDateFrom,
  to: StatDateTo,
});

export const SkuSalesResponseBody = SellerSalesDetail;

export type SkuSalesResponseBody = Static<typeof SkuSalesResponseBody>;

export const ProductProfitDetails = Type.Object({
  productId: Type.Number(),
  name: Type.String(),
  sku: Type.String(),
  listPrice: Type.Number(),
  avgBuyCost: Type.Number(),
  inStockQty: Type.Number(),
  revenue: Type.Number(),
  profit: Type.Number(),
  unitsSold: Type.Number(),
  refunds: Type.Number(),
  image: Type.String(),
})

export type ProductProfitDetails = Static<typeof ProductProfitDetails>;

export const ProductProfitsLimit = Type.Union([Type.Literal(5), Type.Literal(10), Type.Literal(20)]);

export const ProductProfitabilityQuery = Type.Object({
  from: StatDateFrom,
  to: StatDateTo,
  page: Type.Optional(Type.Number({ default: 1 })),
  limit: Type.Optional(ProductProfitsLimit),
  sortBy: Type.Optional(Type.String()),
  search: Type.Optional(Type.String()),
});

export type ProductProfitabilityQuery = Static<typeof ProductProfitabilityQuery>;

export const ProfitabilityByProductResponseBody = Type.Object({
  products: Type.Array(
    ProductProfitDetails
  ),
  total: Type.Number({ default: 0 })
})

export type ProfitabilityByProductResponseBody = Static<typeof ProfitabilityByProductResponseBody>;

export const ProfitabilityByProductOverviewAnalyticsQuery = Type.Object({
  from: StatDateFrom,
  to: StatDateTo,
})

export type profitabilityByProductOverviewAnalyticsQuery = Static<typeof ProfitabilityByProductOverviewAnalyticsQuery>;

export const ProfitabilityByProductOverviewResponseBody = Type.Object({
  totalSKUs: Type.Number(),
  highestProfit: Type.Object({
    productId: Type.Optional(Type.Number()),
    sku: Type.String(),
    productName: Type.String(),
    total: Type.Number(),
    image: Type.String(),
  }),
  highestRevenue: Type.Object({
    productId: Type.Optional(Type.Number()),
    sku: Type.String(),
    productName: Type.String(),
    total: Type.Number(),
    image: Type.String(),
  }),
  highestUnitsSold: Type.Object({
    productId: Type.Optional(Type.Number()),
    sku: Type.String(),
    productName: Type.String(),
    total: Type.Number(),
    image: Type.String(),
  }),
  highestRefunds: Type.Object({
    productId: Type.Optional(Type.Number()),
    sku: Type.String(),
    productName: Type.String(),
    total: Type.Number(),
    image: Type.String(),
  }),
})

export type ProfitabilityByProductOverviewResponseBody = Static<typeof ProfitabilityByProductOverviewResponseBody>;

// for individual sku profitability breakdown
export const ProfitabilityByProductBreakdownByDate = Type.Object({
  date: Type.Number(),
  revenue: Type.Number(),
  fees: Type.Number(),
  profit: Type.Number(),
  sales: Type.Number(),
  refunds: Type.Number(),
  margin: Type.Number(),

  //more breakdown in case needed
  amazonFees: Type.Number(),
  chargebackFees: Type.Number(),
  promotions: Type.Number(),
  deferedAmazonFees: Type.Number(),
  refundCosts: Type.Number(),
  cogs: Type.Number(),
});

export type ProfitabilityByProductBreakdownByDate = Static<typeof ProfitabilityByProductBreakdownByDate>;

export const ProfitabilityByProductBreakdown = Type.Object({
  sku: Type.String(),
  image: Type.String(),
  asin: Type.String(),
  roiPercent: Type.Number(),
  marginPercent: Type.Number(),
  revenuePercent: Type.Number(),
  salesBreakdownByDate: Type.Array(ProfitabilityByProductBreakdownByDate),
  totalRevenue: Type.Number(),
  totalFees: Type.Number(),
  totalProfit: Type.Number(),
  productName: Type.String(),
  totalSales: Type.Number(),
  totalRefunds: Type.Number(),
  averageBuyCost: Type.Number(),
})

export type ProfitabilityByProductBreakdown = Static<typeof ProfitabilityByProductBreakdown>;

export const ProfitabilityByProductBreakdownQuery = Type.Object({
  from: StatDateFrom,
  to: StatDateTo,
})

//profit by asin

export const AsinProfitabilityQuery = Type.Object({
  from: StatDateFrom,
  to: StatDateTo,
  page: Type.Number({ default: 1 }),
  limit: ProductProfitsLimit,
  sortBy: Type.Optional(Type.String()),
  search: Type.Optional(Type.String()),
});

export const AsinProfitDetails = Type.Object({
  name: Type.String(),
  asin: Type.String(),
  listPrice: Type.Number(),
  avgBuyCost: Type.Number(),
  inStockQty: Type.Number(),
  revenue: Type.Number(),
  profit: Type.Number(),
  unitsSold: Type.Number(),
  refunds: Type.Number(),
  image: Type.String(),
})

export type AsinProfitDetails = Static<typeof AsinProfitDetails>;

export const ProfitabilityByAsinResponseBody = Type.Object({
  asins: Type.Array(
    AsinProfitDetails
  ),
  total: Type.Number({ default: 0 })
})

export type ProfitabilityByAsinResponseBody = Static<typeof ProfitabilityByAsinResponseBody>;

export const ProfitabilityByAsinOverviewAnalyticsQuery = Type.Object({
  from: StatDateFrom,
  to: StatDateTo,
})

export type ProfitabilityByAsinOverviewAnalyticsQuery = Static<typeof ProfitabilityByProductOverviewAnalyticsQuery>;

export const ProfitabilityByAsinOverviewResponseBody = Type.Object({
  totalASINs: Type.Number(),
  highestProfit: Type.Object({
    asin: Type.String(),
    productName: Type.String(),
    total: Type.Number(),
    image: Type.String(),
  }),
  highestRevenue: Type.Object({
    asin: Type.String(),
    productName: Type.String(),
    total: Type.Number(),
    image: Type.String(),
  }),
  highestUnitsSold: Type.Object({
    asin: Type.String(),
    productName: Type.String(),
    total: Type.Number(),
    image: Type.String(),
  }),
  highestRefunds: Type.Object({
    asin: Type.String(),
    productName: Type.String(),
    total: Type.Number(),
    image: Type.String(),
  }),
})

export type ProfitabilityByAsinOverviewResponseBody = Static<typeof ProfitabilityByAsinOverviewResponseBody>;

export const ProfitabilityByAsinBreakdownByDate = Type.Object({
  date: Type.Number(),
  revenue: Type.Number(),
  fees: Type.Number(),
  profit: Type.Number(),
  sales: Type.Number(),
  refunds: Type.Number(),
  merchantShippingCost: Type.Number(),
  margin: Type.Number(),
});

export type ProfitabilityByAsinBreakdownByDate = Static<typeof ProfitabilityByAsinBreakdownByDate>;

export const ProfitabilityByAsinBreakdown = Type.Object({
  asin: Type.String(),
  image: Type.String(),
  roiPercent: Type.Number(),
  marginPercent: Type.Number(),
  revenuePercent: Type.Number(),
  salesBreakdownByDate: Type.Array(ProfitabilityByProductBreakdownByDate),
  totalRevenue: Type.Number(),
  totalFees: Type.Number(),
  totalProfit: Type.Number(),
  productName: Type.String(),
  totalSales: Type.Number(),
  totalRefunds: Type.Number(),
  totalShippingCost: Type.Number(),
  averageBuyCost: Type.Number(),
});

export type ProfitabilityByAsinBreakdown = Static<typeof ProfitabilityByAsinBreakdown>;

export const ProfitabilityByAsinBreakdownQuery = Type.Object({
  from: StatDateFrom,
  to: StatDateTo,
})

export type ProfitabilityByAsinBreakdownQuery = Static<typeof ProfitabilityByAsinBreakdownQuery>;

export const OrderItemSalesQuery = Type.Object({
  from: StatDateFrom,
  to: StatDateTo,
});
export const ProfitabilityByCategoryAnalyticsQuery = Type.Object({
  from: StatDateFrom,
  to: StatDateTo,
  page: Type.Number({ default: 1 }),
  limit: ProductProfitsLimit,
  sortBy: Type.Optional(Type.String()),
  search: Type.Optional(Type.String()),
});

export type ProfitabilityByCategoryAnalyticsQuery = Static<typeof ProfitabilityByCategoryAnalyticsQuery>

export const CategoryProfitDetails = Type.Object({
  category: Type.String(),
  revenue: Type.Number(),
  profit: Type.Number(),
  refunds: Type.Number(),
  unitsSold: Type.Number(),
  marginPercentage: Type.Number()
})

export type CategoryProfitDetails = Static<typeof CategoryProfitDetails>

export const CategoryProfitAnalysisSchema = Type.Array(
  CategoryProfitDetails
)

export type CategoryProfitAnalysisResponse = Static<typeof CategoryProfitAnalysisSchema>

export const ProfitabilityByCategoryAnalyticsOverviewQuery = Type.Object({
  from: StatDateFrom,
  to: StatDateTo,
})

export type ProfitabilityByCategoryAnalyticsOverviewQuery = Static<typeof ProfitabilityByCategoryAnalyticsOverviewQuery>

export const ProfitabilityByCategoryOverviewResponseBody = Type.Object({
  total: Type.Number(),
  highestMarginPercentage: Type.Object({
    category: Type.String(),
    value: Type.Number(),
  }),
  highestRefunds: Type.Object({
    category: Type.String(),
    value: Type.Number(),
  }),
  highestProfit: Type.Object({
    category: Type.String(),
    value: Type.Number(),
  }),
  highestRevenue: Type.Object({
    category: Type.String(),
    value: Type.Number(),
  }),
  highestUnitsSold: Type.Object({
    category: Type.String(),
    value: Type.Number(),
  })
})

export type ProfitabilityByCategoryOverviewResponseBody = Static<typeof ProfitabilityByCategoryOverviewResponseBody>

export type OrderItemSalesQuery = Static<typeof OrderItemSalesQuery>;

export const OrderItemSalesItem = Type.Object({
  productId: Type.Number(),
  orderDate: Type.Number(),
  orderId: Type.String(),
  orderStatus: Type.String(),
  image: Type.String(),
  name: Type.String(),
  sku: Type.String(),
  asin: Type.String(),
  fulfillmentChannel: Type.String(),
  totalSales: Type.Number(),
  totalUnits: Type.Number(),
  totalShipped: Type.Number(),
  totalCogs: Type.Number(),
  totalAmazonFees: Type.Number(),
  totalRefundCosts: Type.Number(),
  totalShippingCosts: Type.Number(),
  totalPromotions: Type.Number(),
  totalRefundCount: Type.Number(),
  grossProfit: Type.Number(),
  estimatedPayout: Type.Number(),
  percentageRefunds: Type.Number(),
  margin: Type.Number(),
  roi: Type.Number(),
});

export type OrderItemSalesItem = Static<typeof OrderItemSalesItem>;

export const OrderItemSalesResponseBody = Type.Object({
  orderItems: Type.Array(
    OrderItemSalesItem
  ),
  total: Type.Number({ default: 0 }),
});

export type OrderItemSalesResponseBody = Static<typeof OrderItemSalesResponseBody>;

const CategoryHighlightMetricSchema = Type.Object({
  category: Type.String(),
  value: Type.Number()
});


// Create the generic DynamicHighlights schema factory
const createDynamicHighlightsSchema = <T extends TSchema>(metricSchema: T) => Type.Intersect([
  Type.Object({
    total: Type.Number()
  }),
  Type.Record(
    Type.String(),
    Type.Union([
      Type.Number(),
      metricSchema
    ])
  )
]);

// Use it with specific types
const DynamicHighlightsWithMetricSchema = createDynamicHighlightsSchema(CategoryHighlightMetricSchema);

export type DynamicHighlightsResponse = Static<typeof DynamicHighlightsWithMetricSchema>


export const ProfitabilityByCategoryBreakdown = Type.Object({
  category: Type.String(),
  roiPercent: Type.Number(),
  marginPercent: Type.Number(),
  revenuePercent: Type.Number(),
  salesBreakdownByDate: Type.Array(ProfitabilityByProductBreakdownByDate),
  totalRevenue: Type.Number(),
  totalFees: Type.Number(),
  totalProfit: Type.Number(),
  totalSales: Type.Number(),
  totalRefunds: Type.Number(),
  buyCosts: Type.Number(),
});

export type ProfitabilityByCategoryBreakdown = Static<typeof ProfitabilityByCategoryBreakdown>

export const ProductsSold = Type.Object({
  asin: Type.String(),
  sku: Type.String(),
  name: Type.String(),
  revenue: Type.Number(),
  profit: Type.Number(),
  UnitsSold: Type.Number(),
  refunds: Type.Number(),
  buyCosts: Type.Number(),
  listPrice: Type.Number(),
  image: Type.String(),
})

export type ProductsSold = Static<typeof ProductsSold>

export const ProfitabilityByCategoryProductsSold = Type.Object({
  category: Type.String(),
  products: Type.Array(ProductsSold),
  totalProducts: Type.Number(),
})

export type ProfitabilityByCategoryProductsSold = Static<typeof ProfitabilityByCategoryProductsSold>

export const ProfitOverviewQuery = Type.Object({
  currentFrom: StatDateFrom,
  currentTo: StatDateTo,
  previousFrom: StatDateFrom,
  previousTo: StatDateTo,
});

export type ProfitOverviewQuery = Static<typeof ProfitOverviewQuery>;

export const ProfitOverviewPeriod = Type.Object({
  dateRange: StatDateRange,
  // unitSnapshot: Type.Object({
  //   unitsSold: Type.Number(),
  //   unitsShipped: Type.Number(),
  //   unitsPendingShipment: Type.Number(),
  // }),
  // marginSnapshot: Type.Object({
  //   margin: Type.Number(),
  //   netProfit: Type.Number(),
  //   sales: Type.Number(),
  // }),
  // roiSnapshot: Type.Object({
  //   roi: Type.Number(),
  //   grossProfit: Type.Number(),
  //   costs: Type.Number(),
  // }),
  breakdown: Type.Object({
    sales: Type.Object({
      total: Type.Number(),
      data: Type.Array(StatDataSeries)
    }),
    unitsSold: Type.Object({
      total: Type.Number(),
      data: Type.Array(StatDataSeries)
    }),
    // grossProfit: Type.Object({
    //   total: Type.Number(),
    //   data: Type.Array(StatDataSeries)
    // }),
    costs: Type.Object({
      total: Type.Number(),
      data: Type.Array(StatDataSeries)
    }),
  }),
});

export type ProfitOverviewPeriod = Static<typeof ProfitOverviewPeriod>;

export const ProfitOverview = Type.Object({
  currentPeriod: ProfitOverviewPeriod,
  previousPeriod: ProfitOverviewPeriod,
});

export type ProfitOverview = Static<typeof ProfitOverview>;

export const ProfitOverViewSummery = Type.Object({
    // first summary 
    totalUnitsSoldPercentageDifFromOtherPeriod: Type.Number(),
    totalUnitsSold: Type.Number(),
    totalUnitsShipped: Type.Number(),
    totalUnitsPending: Type.Number(),
    totalUnitsPromoOrder: Type.Number(),
    totalBuyCosts: Type.Number(),

    //second summary
    totalNetMarginPercentageDifFromOtherPeriod: Type.Number(),
    totalNetMargin: Type.Number(),
    totalNetProfit: Type.Number(),
    totalRevenue: Type.Number(),

    //third summary
    roiPercentDifFromOtherPeriod: Type.Number(),
    roi: Type.Number(),
    totalGrossProfit: Type.Number(),
    totalUnitCostsPlusShipping: Type.Number(),
})

export type ProfitOverViewSummery = Static<typeof ProfitOverViewSummery>;

export const ProfitOverViewSummaries = Type.Object({
  currentPeriod: ProfitOverViewSummery,
  previousPeriod: ProfitOverViewSummery,
})

export type ProfitOverViewSummaries = Static<typeof ProfitOverViewSummaries>;


export const GraphData = Type.Object({
  date: Type.Number(),
  previousValue: Type.Number(),
  currentValue: Type.Number(),
  formattedDate: Type.Optional(Type.String()),
})

export type GraphData = Static<typeof GraphData>;

export const GraphDataSeries = Type.Object({
  data: Type.Array(GraphData),
  currentTotal: Type.Number(),
  currentPercentageDiffToPreviousPeriod: Type.Number(),
  previousTotal: Type.Number(),
  previousPercentageDiffToCurrentPeriod: Type.Number(),
})

export type GraphDataSeries = Static<typeof GraphDataSeries>;

export const ProfitOverviewGraph = Type.Object({
  profitGraph: GraphDataSeries,
  revenueGraph: GraphDataSeries,
  unitsSoldGraph: GraphDataSeries,
  buyCostsGraph: GraphDataSeries,
})

export type ProfitOverviewGraph = Static<typeof ProfitOverviewGraph>;








export const ProfitabilityGraphData = Type.Object({
  date: Type.Number(),
  revenue: Type.Number(),
  fees: Type.Number(),
  profit: Type.Number(),
  sales: Type.Number(),
  refunds: Type.Number(),
  margin: Type.Number(),

  //more breakdown in case needed
  amazonFees: Type.Number(),
  chargebackFees: Type.Number(),
  promotions: Type.Number(),
  deferedAmazonFees: Type.Number(),
  refundCosts: Type.Number(),
  cogs: Type.Number(),
});

export type ProfitabilityGraphData = Static<typeof ProfitabilityGraphData>;


